import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { ZodTypeAny } from 'zod'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import CopyableApiKeyValue from 'app/frontend/base/components/CopyableApiKeyValue'

const THEME_KEYS = [
  'themeMode',
  'headerShowName',
  'generalTheme',
  'lightModeTheme',
  'darkModeTheme',
  'lightModeBodyBackgroundPath',
  'darkModeBodyBackgroundPath',
  'lightModeHeaderLogoPath',
  'darkModeHeaderLogoPath',
  'headerConfig',
] as const

type ThemeConfig = Record<string, unknown>

const pickThemeConfig = (values: ThemeConfig): ThemeConfig =>
  Object.fromEntries(
    THEME_KEYS.filter((key) => key in values).map((key) => [key, values[key]]),
  )

const encodeTheme = (config: ThemeConfig): string => {
  const bytes = new TextEncoder().encode(JSON.stringify(config))
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('')
  return btoa(binary)
}

const decodeTheme = (encoded: string): ThemeConfig => {
  const binary = atob(encoded.trim())
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
  const parsed = JSON.parse(new TextDecoder().decode(bytes))

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('Decoded theme is not an object')
  }

  return parsed as ThemeConfig
}

export const ExportThemeButton = () => {
  const [open, setOpen] = useState(false)
  const form = useFormContext()

  const encoded = open
    ? encodeTheme(pickThemeConfig(form.getValues('customDomainAttributes')))
    : ''

  const handleCopy = () => {
    navigator.clipboard.writeText(encoded)
    setOpen(false)
    toast.success('Copied theme to clipboard')
  }

  return (
    <>
      <Button
        type='button'
        variant='outline'
        size='sm'
        onClick={() => setOpen(true)}
      >
        Export theme
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Export theme</DialogTitle>
            <DialogDescription>
              Copy the value below to export your custom domain&apos;s theme.
              You can import it into another team or guild to reuse the same
              look.
            </DialogDescription>
          </DialogHeader>

          <CopyableApiKeyValue
            value={encoded}
            tooltipContent='Click to copy'
            cardClassName='w-full break-all py-2 px-3 max-h-40 overflow-y-auto'
            onCopiedChange={(copied) =>
              copied ? toast.success('Copied theme to clipboard') : undefined
            }
          />

          <DialogFooter>
            <Button
              type='button'
              size='sm'
              variant='outline'
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type='button' size='sm' onClick={handleCopy}>
              Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export const ImportThemeButton = ({
  formSchema,
}: {
  formSchema: ZodTypeAny
}) => {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')
  const [error, setError] = useState<string>()
  const form = useFormContext()

  const close = () => {
    setOpen(false)
    setValue('')
    setError(undefined)
  }

  const handleApply = () => {
    let decoded: ThemeConfig

    try {
      decoded = decodeTheme(value)
    } catch {
      setError('This is not a valid exported theme. Please check the value.')
      return
    }

    const result = formSchema.safeParse({ customDomainAttributes: decoded })

    if (!result.success) {
      setError('This is not a valid exported theme. Please check the value.')
      return
    }

    Object.entries(pickThemeConfig(decoded)).forEach(([key, fieldValue]) => {
      form.setValue(`customDomainAttributes.${key}`, fieldValue, {
        shouldDirty: true,
      })
    })

    close()
    toast.success('Imported theme')
  }

  return (
    <>
      <Button
        type='button'
        variant='outline'
        size='sm'
        onClick={() => setOpen(true)}
      >
        Import
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => (next ? setOpen(true) : close())}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import theme</DialogTitle>
            <DialogDescription>
              Paste a previously exported theme below to apply it to this custom
              domain. You will still need to save your changes afterwards.
            </DialogDescription>
          </DialogHeader>

          <div className='flex flex-col gap-1'>
            <Textarea
              value={value}
              onChange={(e) => {
                setValue(e.target.value)
                setError(undefined)
              }}
              placeholder='Paste your exported theme here'
              className='min-h-32 break-all'
            />
            {error ? (
              <span className='text-destructive-foreground text-sm'>
                {error}
              </span>
            ) : null}
          </div>

          <DialogFooter>
            <Button type='button' size='sm' variant='outline' onClick={close}>
              Cancel
            </Button>
            <Button
              type='button'
              size='sm'
              disabled={!value.trim()}
              onClick={handleApply}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
